import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Check, Clock, Image as ImageIcon, Loader2, X, FileEdit } from "lucide-react";
import { listAllListingRevisions, reviewListingRevision } from "@/lib/partner-revisions.functions";

export const Route = createFileRoute("/_authenticated/admin/listing-revisions")({
  head: () => ({
    meta: [
      { title: "Listing Change Requests — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminListingRevisions,
});

type Row = {
  id: string;
  client_id: string;
  status: string;
  changes: Record<string, unknown> | null;
  current_draft?: Record<string, unknown> | null;
  partner_note: string | null;
  admin_note: string | null;
  created_at: string;
  applied_at: string | null;
  crm_clients?: { id: string; name: string | null; company_name: string | null } | null;
};

const FIELD_LABELS: Record<string, string> = {
  name: "Business name", address: "Address", city: "City", website: "Website",
  instagram: "Instagram", facebook: "Facebook", phone: "Phone", email: "Email",
  description: "Description", category: "Category", subcategory: "Subcategory",
  area: "Area", vibe: "Vibe", price: "Price range", tags: "Tags",
  overview_body: "Overview", details_body: "Details", tickets_info: "Tickets info",
  schedule_info: "Schedule info", buy_ticket_url: "Ticket link", cta: "Call to action",
  lat: "Latitude", lng: "Longitude",
};
const fieldLabel = (k: string) => FIELD_LABELS[k] ?? k.replace(/_/g, " ");

function renderValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v, null, 2);
  return String(v);
}


const STATUSES = ["submitted", "approved", "rejected", "cancelled"] as const;

function AdminListingRevisions() {
  const [filter, setFilter] = useState<string>("submitted");
  const qc = useQueryClient();
  const listFn = useServerFn(listAllListingRevisions);
  const reviewFn = useServerFn(reviewListingRevision);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin", "listing-revisions", filter],
    queryFn: () => listFn({ data: filter ? { status: filter } : {} }) as Promise<Row[]>,
    refetchInterval: 8_000,
  });

  const review = useMutation({
    mutationFn: (v: { id: string; action: "approve" | "reject"; admin_note?: string }) =>
      reviewFn({ data: v }),
    onSuccess: (_r, v) => {
      toast.success(v.action === "approve" ? "Changes published" : "Changes rejected");
      qc.invalidateQueries({ queryKey: ["admin", "listing-revisions"] });
    },
    onError: (e: unknown) => toast.error((e as Error)?.message || "Failed"),
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-900">Listing Change Requests</h1>
          <p className="text-sm text-slate-500 mt-1">Review partner edits to any listing field. Approving publishes the changes to the live listing.</p>
        </header>

        <div className="flex flex-wrap gap-2 mb-5">
          <button onClick={() => setFilter("")} className={`px-3 py-1.5 rounded-xl text-sm ${filter === "" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>All</button>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-xl text-sm capitalize ${filter === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>{s}</button>
          ))}
        </div>

        {isLoading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {!isLoading && rows.length === 0 && <div className="text-sm text-slate-500">No revisions.</div>}

        <div className="space-y-3">
          {rows.map((r) => {
            const changes = (r.changes || {}) as Record<string, unknown>;
            const keys = Object.keys(changes);
            const hasHours = keys.includes("hours");
            const hasImages = keys.includes("images");
            const otherKeys = keys.filter((k) => k !== "hours" && k !== "images");
            const images = (changes.images as Array<{ url: string; name?: string }> | undefined) ?? [];
            return (
              <div key={r.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">
                      {r.crm_clients?.company_name || r.crm_clients?.name || "Partner"}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Submitted {new Date(r.created_at).toLocaleString()}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {hasHours && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                          <Clock className="h-3 w-3" /> Hours
                        </span>
                      )}
                      {hasImages && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                          <ImageIcon className="h-3 w-3" /> Photos
                        </span>
                      )}
                      {otherKeys.map((k) => (
                        <span key={k} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                          <FileEdit className="h-3 w-3" /> {fieldLabel(k)}
                        </span>
                      ))}
                    </div>
                    {r.partner_note && (
                      <div className="mt-3 text-sm text-slate-700 whitespace-pre-wrap">{r.partner_note}</div>
                    )}
                  </div>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 capitalize">{r.status}</span>
                </div>

                {otherKeys.length > 0 && (
                  <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 divide-y divide-slate-100">
                    {otherKeys.map((k) => {
                      const before = (r.current_draft ?? {})[k];
                      const after = changes[k];
                      return (
                        <div key={k} className="px-3 py-2">
                          <div className="text-xs uppercase tracking-wider text-slate-500">{fieldLabel(k)}</div>
                          <div className="mt-1 grid sm:grid-cols-2 gap-2 text-sm">
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-slate-400">Before</div>
                              <div className="text-slate-500 line-through whitespace-pre-wrap break-words">{renderValue(before)}</div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-emerald-600">After</div>
                              <div className="text-slate-900 whitespace-pre-wrap break-words">{renderValue(after)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {hasHours && (
                  <details className="mt-3">
                    <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-800">Show hours diff</summary>
                    <pre className="mt-2 text-xs bg-slate-50 rounded-xl p-3 overflow-auto border border-slate-100">{JSON.stringify(changes.hours, null, 2)}</pre>
                  </details>
                )}
                {hasImages && Array.isArray(images) && (
                  <div className="mt-3">
                    <div className="text-xs text-slate-500 mb-2">Proposed photos ({images.length})</div>
                    <div className="flex gap-2 flex-wrap">
                      {images.map((img, i) => (
                        <img key={i} src={img.url} alt={img.name || ""} className="w-20 h-20 object-cover rounded-xl border border-slate-200" />
                      ))}
                    </div>
                  </div>
                )}


                {r.status === "submitted" && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => review.mutate({ id: r.id, action: "approve" })}
                      className="inline-flex items-center gap-1 rounded-xl bg-emerald-600 text-white text-xs font-medium px-3 py-1.5 hover:bg-emerald-700"
                    >
                      <Check className="h-3 w-3" /> Approve & Publish
                    </button>
                    <button
                      onClick={() => {
                        const note = prompt("Reason for rejection (visible to partner):") || undefined;
                        review.mutate({ id: r.id, action: "reject", admin_note: note });
                      }}
                      className="inline-flex items-center gap-1 rounded-xl border border-slate-300 bg-white text-xs font-medium px-3 py-1.5 text-slate-700 hover:bg-slate-100"
                    >
                      <X className="h-3 w-3" /> Reject
                    </button>
                  </div>
                )}
                {r.admin_note && (
                  <div className="mt-3 text-xs text-slate-600">Reviewer note: {r.admin_note}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
